<script setup>
import { reactive, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { NodeSelection } from '@tiptap/pm/state'
import { BubbleMenu } from '@tiptap/vue-3/menus'
import { elementAnchor, menuOptions } from './floatingMenu.js'
import { canEditChart, isEditingChart, startChartEdit } from './charts/chartEditing.js'
import { useChartEditSession } from '@/composables/useChartEditSession.js'

// RED-317 PR 3b — a locked card (a Word chart or other content the Studio
// keeps verbatim, contract §6) once clicked: move it up or down, or delete
// it. Its content is changed in Word (Laadi alla Wordina → Laadi Wordist),
// which the card itself says while editing. A drawn chart is edited here
// too (RED-317 PR 3d-A): "Muuda andmeid" opens its inline editor, and the
// menu steps aside while it is open.
const props = defineProps({
  editor: { type: Object, required: true },
  // The element the canvas scrolls in (an Element or window); read when the menu mounts.
  scrollTarget: { type: Object, default: null },
})

const { t } = useI18n()

const can = reactive({ up: false, down: false, editData: false, move: true })

// A selected Excel object (an inline rawRun, RED-317 PR 3d-B) gets the menu
// too when its cells can be edited: "Muuda andmeid" and delete; it moves
// with its text, not as a block.
const shouldShow = ({ editor: instance, state }) => {
  if (!instance.isEditable || !(state.selection instanceof NodeSelection) || isEditingChart(instance)) return false
  const name = state.selection.node.type.name
  const object = name === 'rawRun' && canEditChart(instance, state.selection.from)
  if (name !== 'rawBlock' && !object) return false
  can.move = !object
  can.up = !object && instance.can().moveSelectedBlock(-1)
  can.down = !object && instance.can().moveSelectedBlock(1)
  can.editData = canEditChart(instance, state.selection.from)
  return true
}

// Anchor: the selected card's DOM node (an object's preview frame: its wrapper has no box).
const cardVirtualElement = () => {
  const { state, view } = props.editor
  let dom = state.selection instanceof NodeSelection ? view.nodeDOM(state.selection.from) : null
  if (dom instanceof HTMLElement && dom.classList.contains('studio-object-wrap')) dom = dom.querySelector('.studio-object-preview') ?? dom
  return dom instanceof HTMLElement ? elementAnchor(dom) : undefined
}

const move = (direction) => props.editor.chain().focus().moveSelectedBlock(direction).run()

const remove = () => props.editor.chain().focus().deleteSelection().run()

// The menu decides on selection and document changes only: it is told to
// step aside when a chart's editor opens, and to come back when it closes.
const PLUGIN_KEY = 'studioBlockMenu'
const session = useChartEditSession(() => props.editor)
watch(
  () => Boolean(session.value),
  (editing) => {
    const { view, state } = props.editor
    if (!view || view.isDestroyed) return
    const action = editing ? 'hide' : shouldShow({ editor: props.editor, state }) ? 'show' : null
    if (action) view.dispatch(state.tr.setMeta(PLUGIN_KEY, action).setMeta('addToHistory', false))
  },
)

const editData = () => {
  const { selection } = props.editor.state
  if (selection instanceof NodeSelection) startChartEdit(props.editor, selection.from)
}

const buttonClass =
  'flex h-7 w-7 cursor-pointer items-center justify-center rounded-full border-none bg-transparent text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-35'
</script>

<template>
  <BubbleMenu
    :editor="editor"
    :plugin-key="PLUGIN_KEY"
    :should-show="shouldShow"
    :get-referenced-virtual-element="cardVirtualElement"
    :update-delay="50"
    :options="menuOptions(scrollTarget)"
  >
    <div class="flex items-center gap-0.5 rounded-full border border-gray-200 bg-white px-1.5 py-1 shadow-lg" role="toolbar" :aria-label="t('messages.editor.blockMenu')" data-testid="studio-block-menu">
      <template v-if="can.editData">
        <button
          type="button"
          class="flex h-7 cursor-pointer items-center gap-1.5 rounded-full border-none bg-transparent px-2.5 text-[13px] text-gray-700 transition-colors hover:bg-gray-100 hover:text-gray-900"
          data-testid="studio-block-edit-data"
          @click="editData"
        >
          <span class="pi pi-table text-xs" aria-hidden="true" />{{ t('messages.editor.blockEditData') }}
        </button>
        <span class="mx-0.5 h-3.5 w-px bg-gray-200" aria-hidden="true" />
      </template>
      <template v-if="can.move">
        <button v-tooltip.top="t('messages.editor.blockUp')" type="button" :class="buttonClass" :disabled="!can.up" :aria-label="t('messages.editor.blockUp')" data-testid="studio-block-up" @click="move(-1)">
          <span class="pi pi-arrow-up text-xs" />
        </button>
        <button v-tooltip.top="t('messages.editor.blockDown')" type="button" :class="buttonClass" :disabled="!can.down" :aria-label="t('messages.editor.blockDown')" data-testid="studio-block-down" @click="move(1)">
          <span class="pi pi-arrow-down text-xs" />
        </button>
        <span class="mx-0.5 h-3.5 w-px bg-gray-200" aria-hidden="true" />
      </template>
      <button v-tooltip.top="t('messages.editor.blockDelete')" type="button" :class="buttonClass" :aria-label="t('messages.editor.blockDelete')" data-testid="studio-block-delete" @click="remove">
        <span class="pi pi-trash text-xs" />
      </button>
    </div>
  </BubbleMenu>
</template>
